import React, { useEffect, useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import SearchBar from '../../components/searchBar/SearchBar';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/buttons/Button';
import { toast } from 'react-toastify';
import { BiEdit, BiLink, BiRefresh, BiMap, BiListUl, BiPlus } from 'react-icons/bi';
import CustomLoading from '../../components/customLoading/CustomLoading';
import Table from '../../components/tables/Table';
import Pagination from '../../components/tables/pagination/Pagination';
import { getShops, patchShopCrud } from '../../services/shop';
import GalleryComponent from '../../components/GalleryComponent';
import { Check } from 'lucide-react';
import ImageZoom from '../../components/ImageZoom/ImageZoom';
import { CircularProgress } from '@mui/material';

import MapComponent from '../../components/MapComponent';
import { statesOfIndia } from '../../constants/states';
import Dropdown from '../../components/inputFields/DropDown';
import Tab from '../../components/tabs/Tab';
import ShowModal from '../../utils/ShowModal';
import UserOrderHistory from '../sales/UserOrderHistory';
import { CgClose } from 'react-icons/cg';

function Shop() {
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const headers = [
        { label: "Name", key: "name" },
        { label: "Amount", key: "total_paid_amount" },
        // { label: "Gallery", key: "images" },
        { label: "Approv. Img.", key: "approving_image" },
        // { label: "Reg. No.", key: "business_registration_number" },
        // { label: "Trade Lis. No.", key: "trade_license_number" },
        // { label: "Tax ID", key: "tax_id" },
        // { label: "Contact", key: "contact_information" },
        { label: "Address", key: "address" },
        { label: "Page", key: "link" },
        { label: "Owner Name", key: "owner_name" },
        { label: "Owner Number", key: "owner_mobile_number" },
        // { label: "YOE", key: "year_of_establishment" },
        // { label: "Contact", key: "contact_information" },
        { label: "Action", key: "action" },
        { label: "Approval", key: "approve_reject" },
    ];

    const [searchText, setSearchText] = useState("");

    const pageSize = 10;

    const handleSearch = (query) => {
        setSearchText(query);
        fetchData(`?search=${query}&approved=${activeTab}&paid=${filterPaidUsers}&state=${filteredState}`);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const fetchData = async (query) => {
        setLoading(true);
        try {
            let res = await getShops(query);
            if (res.status === 200) {
                const data = res.data;
                setData(data);
            } else {
                toast.error("Unable to fetch Data");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error in Manufacturers:", error);
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = () => {
        setSearchText("");
        setFilterPaidUsers("");
        setFilteredState("");
        setActiveTab("true");
        fetchData(`?approved=true`);
    };

    useEffect(() => {
        fetchData(`?approved=${activeTab}&state=${filteredState}`);
    }, []);

    const [isApprovalPending, setIsApprovalPending] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const handleApproveReject = async (item, approved) => {
        try {
            setIsApprovalPending(true);
            setSelectedItemId(item.id);
            const data = new FormData();
            data.append("approved", approved);
            const response = await patchShopCrud({ data: data, id: item.id });
            if (response.status === 200) {
                toast.success("Shop Approved Successfully");
                fetchData(`?search=${searchText}&page=${currentPage}&approved=${activeTab}&paid=${filterPaidUsers}&state=${filteredState}`);
            } else {
                toast.error("Unable to approve shop");
            }
        } catch (error) {
            console.error("Error in Manufacturers:", error);
        } finally {
            setIsApprovalPending(false);
            setSelectedItemId(null);
        }
    };

    const [activeTab, setActiveTab] = useState("true");
    const [filterPaidUsers, setFilterPaidUsers] = useState("");
    const [filteredState, setFilteredState] = useState("");

    const handleChangeActiveTab = async (item) => {
        setActiveTab(item);
        fetchData(`?search=${searchText}&approved=${item}&paid=${filterPaidUsers}&state=${filteredState}`);
    };

    const handlePaidUsers = (newValue) => {
        setFilterPaidUsers(newValue);
        const query = `?search=${searchText}&approved=${activeTab}&paid=${newValue}&state=${filteredState}`;
        fetchData(query);
    };

    const handleStateChange = (value) => {
        setFilteredState(value);
        fetchData(`?search=${searchText}&approved=${activeTab}&paid=${filterPaidUsers}&state=${value}`);
    };

    // Toggle between map and list view
    const toggleViewMode = () => {
        setViewMode(viewMode === 'list' ? 'map' : 'list');
    };

    // Filter shops based on active tab
    const getFilteredShops = () => {
        if (!data?.results) return [];

        return data.results.filter(shop => {
            if (activeTab === "true") {
                return shop.approved === true;
            } else {
                return shop.approved === false;
            }
        });
    };

    // Prepare map data with shop locations for MapComponent
    const getMapData = () => {
        const filteredShops = getFilteredShops();
        console.log('Filtered shops:', filteredShops);

        const mapData = filteredShops
            .filter(shop => shop.lat && shop.lon) // Only shops with coordinates
            .map(shop => ({
                lat: parseFloat(shop.lat),
                lng: parseFloat(shop.lon),
                // Use custom marker data instead of total_paid_amount files
                markerData: {
                    approved: shop.approved,
                    name: shop.name,
                    owner_name: shop.owner_name,
                    address: shop.address,
                    logo: shop.logo,
                    id: shop.id
                },
                label: `${shop.name} - ${shop.owner_name}`,
                shop: shop // Store full shop data for click handling
            }));

        console.log('Map data:', mapData);
        return mapData;
    };

    // Handle map marker click to open edit screen
    const handleMapMarkerClick = (marker) => {
        if (marker.shop) {
            // Navigate to edit screen with shop data using state
            navigate(`/bazzar/shop/${marker.shop.name}/`, { state: marker.shop });
        }
    };

    // Get map center based on shops data
    const getMapCenter = () => {
        const mapData = getMapData();
        if (mapData.length === 0) return [22.7196, 75.8577]; // Default center (India)

        const latSum = mapData.reduce((sum, marker) => sum + marker.lat, 0);
        const lngSum = mapData.reduce((sum, marker) => sum + marker.lng, 0);

        return [latSum / mapData.length, lngSum / mapData.length];
    };

    const [modalIsOpen2, setIsOpen2] = useState(false);
    const [openingItem2, setOpeningItem2] = useState([]);

    const closeModal = () => {
        setIsOpen2(false);
    };

    const openModal2 = (data) => {
        setIsOpen2(true);
        setOpeningItem2(data);
    };

    return (<>
        {/* Modern Header Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-xl">
                        <BiMap className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                        <Breadcrumbs items={[
                            { name: 'Home', link: '/dashboard' },
                            { name: 'Bazaar' },
                            { name: 'Shops' },
                        ]} />
                        <h1 className="text-2xl font-bold text-gray-800 mt-1">Shop Management</h1>
                        <p className="text-gray-600 text-sm">Manage and monitor all registered shops</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/bazzar/shop/add">
                        <Button
                            type="button"
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200"
                        >
                            <BiPlus size={20} />
                            Add Shop
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
        {/* Modern Filters Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
            <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
                <div className="flex flex-wrap items-center gap-4 flex-1">
                    <div className="flex-1 min-w-64">
                        <SearchBar
                            placeholder="Search by shop name..."
                            onSearch={handleSearch}
                            value={searchText}
                            className="h-10"
                        />
                    </div>

                    {/* State Filter Dropdown */}
                    <div className="w-44">
                        <Dropdown
                            placeholder="Filter by State"
                            options={statesOfIndia.map((e) => ({ label: e, value: e }))}
                            onChange={handleStateChange}
                            value={filteredState}
                            needSearch
                            className="h-10 text-sm"
                        />
                    </div>

                    {/* Three-Way Paid Users Switch */}
                    <div className="flex items-center space-x-3 bg-gray-50 px-2 py-1 rounded-lg">
                        <div className="flex flex-col items-center">
                            <span
                                className={`text-xs font-medium cursor-pointer transition-colors ${filterPaidUsers === "" ? 'text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
                                onClick={() => handlePaidUsers("")}
                                title="Show All Users"
                            >
                                All
                            </span>
                            <div className="flex items-center space-x-2">
                                <span
                                    className={`text-xs font-medium cursor-pointer transition-colors ${filterPaidUsers === false ? 'text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
                                    onClick={() => handlePaidUsers(false)}
                                    title="Show Unpaid Users"
                                >
                                    Unpaid
                                </span>
                                <div className="relative inline-flex items-center h-7 rounded-full w-20 bg-gray-200 p-1 transition-all duration-200">
                                    <div
                                        className={`absolute w-5 h-5 bg-white rounded-full shadow-md transition-all duration-200 cursor-pointer ${filterPaidUsers === false ? 'translate-x-0' :
                                            filterPaidUsers === "" ? 'translate-x-7' : 'translate-x-14'
                                            }`}
                                        onClick={() => {
                                            if (filterPaidUsers === false) {
                                                handlePaidUsers("");
                                            } else if (filterPaidUsers === "") {
                                                handlePaidUsers(true);
                                            } else {
                                                handlePaidUsers(false);
                                            }
                                        }}
                                    />
                                    <div className="flex justify-between w-full px-1">
                                        <div
                                            className={`w-1 h-1 rounded-full cursor-pointer ${filterPaidUsers === false ? 'bg-blue-600' : 'bg-gray-400'}`}
                                            onClick={() => handlePaidUsers(false)}
                                            title="Show Unpaid Users"
                                        />
                                        <div
                                            className={`w-1 h-1 rounded-full cursor-pointer ${filterPaidUsers === "" ? 'bg-blue-600' : 'bg-gray-400'}`}
                                            onClick={() => handlePaidUsers("")}
                                            title="Show All Users"
                                        />
                                        <div
                                            className={`w-1 h-1 rounded-full cursor-pointer ${filterPaidUsers === true ? 'bg-blue-600' : 'bg-gray-400'}`}
                                            onClick={() => handlePaidUsers(true)}
                                            title="Show Paid Users"
                                        />
                                    </div>
                                </div>
                                <span
                                    className={`text-xs font-medium cursor-pointer transition-colors ${filterPaidUsers === true ? 'text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
                                    onClick={() => handlePaidUsers(true)}
                                    title="Show Paid Users"
                                >
                                    Paid
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Reset Filters */}
                    {(searchText.length !== 0 || filterPaidUsers !== "" || filteredState !== "" || activeTab !== "true") && (
                        <button
                            onClick={resetFilters}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                            title="Reset all filters"
                        >
                            <BiRefresh size={20} />
                        </button>
                    )}
                </div>

                {/* View Toggle Button */}
                <div className="flex items-center gap-3">
                    <Button
                        type="button"
                        onClick={toggleViewMode}
                        className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-all duration-200 ${viewMode === 'map'
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                            }`}
                    >
                        {viewMode === 'list' ? (
                            <>
                                <BiMap size={18} />
                                Map View
                            </>
                        ) : (
                            <>
                                <BiListUl size={18} />
                                List View
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
        <div className="mb-2">
            <Tab
                activeTab={activeTab}
                onClick={handleChangeActiveTab}
                tabsList={[
                    {
                        id: "true",
                        name: "Approved",
                    },
                    {
                        id: "false",
                        name: "Pending | Rejected",
                    }
                ]}
            />
        </div>
        {/* Content Section */}
        {loading ? (
            <div className="flex justify-center items-center py-16">
                <div className="text-center">
                    <CustomLoading size={80} />
                    <p className="text-gray-600 mt-4">Loading shops data...</p>
                </div>
            </div>
        ) : !data?.results || data.results.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BiMap size={32} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No shops found</h3>
                <p className="text-gray-600 mb-4">
                    {filteredState ? `No shops found in ${filteredState}` : 'No shops match your current filters'}
                </p>
                <button
                    onClick={resetFilters}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <BiRefresh size={16} />
                    Reset Filters
                </button>
            </div>
        ) : (
            <div className="space-y-6">
                {/* Pagination - Available in both views */}
                {data.count !== 0 && (
                    <div className="mb-4">
                        <Pagination
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalEntries={data?.count}
                            onPageChange={handlePageChange}
                            data={data}
                            setData={setData}
                        />
                    </div>
                )}

                {/* Map View */}
                {viewMode === 'map' && (
                    <div className="bg-white rounded-lg border p-4">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                {activeTab === "true" ? "Approved" : "Pending"} Shops
                                {filteredState && ` - ${filteredState}`} - Map View
                            </h3>
                            <p className="text-sm text-gray-600">
                                Click on markers to view shop details, then use the edit button in the tooltip
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    <span>Approved Shops</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                                    <span>Pending Shops</span>
                                </div>
                            </div>
                        </div>
                        <div className="h-96 rounded-lg overflow-hidden border">
                            <MapComponent
                                markersList={getMapData()}
                                mapLocation={getMapCenter()}
                                zoomLevel={8}
                                height="100%"
                                isEditMode={false}
                                onMarkerClick={handleMapMarkerClick}
                            />
                        </div>
                    </div>
                )}

                {/* List View */}
                {viewMode === 'list' && (
                    <Table
                        headers={headers}
                        rows={data?.results?.map((item, index) => (
                            {
                                name: <p onClick={() => openModal2(item)} className='cursor-pointer'>{item.name}</p>,
                                total_paid_amount: <p onClick={() => openModal2(item)} className='cursor-pointer'>₹{item.total_paid_amount}</p>,
                                approving_image: <ImageZoom src={item.logo} className='w-12 aspect-square rounded-sm border object-contain' />,
                                images: <GalleryComponent openingComponent={item.images.length > 0 ? <img src={item.images[0].total_paid_amount} className='w-12 aspect-square rounded-sm border object-contain' /> : <p>NA</p>} className='rounded-full aspect-square max-w-28 border border-primary' images={item.images} />,
                                business_registration_number: <p onClick={() => openModal2(item)} className='cursor-pointer'>{item.business_registration_number}</p>,
                                tax_id: <p onClick={() => openModal2(item)} className='cursor-pointer'>{item.tax_id}</p>,
                                owner_name: <p onClick={() => openModal2(item)} className='cursor-pointer truncate max-w-40' title={item.owner_name}>{item.owner_name}</p>,
                                owner_mobile_number: <p onClick={() => openModal2(item)} className='cursor-pointer'>{item.owner_mobile_number}</p>,
                                year_of_establishment: <p onClick={() => openModal2(item)} className='cursor-pointer'>{item.year_of_establishment}</p>,
                                contact_information: <p onClick={() => openModal2(item)} className='cursor-pointer'>{item.contact_information}</p>,
                                address: <span className='flex flex-row gap-1 items-center'><p className="max-w-48 truncate cursor-help" title={item?.address?.state || ""}>{item?.address?.state || ""}</p></span>,
                                link: <Link to={`https://bazaar.rashailagro.in/shop/seller/${item.slug}/`} target='_blank' ><BiLink size={24} color='blue' /></Link>,
                                action: <button
                                    onClick={() => navigate(`/bazzar/shop/${item.name}/`, { state: item })}
                                    className="text-green-600 hover:text-green-800 transition-colors"
                                >
                                    <BiEdit size={24} />
                                </button>,
                                approve_reject: <div className="flex flex-row gap-3 items-center justify-center text-center">
                                    {
                                        item.approved ?
                                            <>
                                                <span type="button" className="hover:opacity-70 bg-primary text-white py-1 px-1.5 text-xs font-bold rounded-full">Approved</span>
                                                {
                                                    isApprovalPending && item.id === selectedItemId ?
                                                        <CircularProgress size={20} color='#2FB95D' />
                                                        :
                                                        <span type="button" onClick={() => handleApproveReject(item, false)} className="hover:opacity-70 text-white bg-red-600 p-1 rounded-full cursor-pointer"><CgClose size={20} /></span>}
                                            </>
                                            :
                                            isApprovalPending && item.id === selectedItemId ?
                                                <CircularProgress size={20} color='#2FB95D' />
                                                :
                                                <>
                                                    <span type="button" onClick={() => handleApproveReject(item, true)} className="hover:opacity-70 text-white bg-primary p-1 rounded-full cursor-pointer"><Check size={20} /></span>
                                                    <span type="button" onClick={() => handleApproveReject(item, false)} className="hover:opacity-70 text-white bg-red-600 p-1 rounded-full cursor-pointer"><CgClose size={20} /></span>
                                                </>
                                    }
                                </div>,
                                rowBgColor: item.approved ? "" : "bg-red-200"
                            }))}
                    />
                )}
            </div>
        )}
        <ShowModal contentLabel={"Orders History"} isOpen={modalIsOpen2} modalWidth="50%" isSidebar={true} onRequestClose={closeModal} componentToPass={
            <UserOrderHistory user_id={openingItem2.owner} showTopItems={false} />
        } />
    </>
    );
}

export default Shop;